/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { BigNumber } from '@ethersproject/bignumber';
import { formatBytes32String } from '@ethersproject/strings';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { setProviderEventsHandler } from '@manifoldxyz/manifold-dropsitetools-lib';
import collectors, { IMerkleInfo } from '@/api/collectors';
import ClaimExtensionContract, {
  StorageProtocol
} from '@/classes/claimExtensionContract';
import { DELEGATION_REGISTRY_ADDRESS, NETWORK_ID } from '@/common/constants';
import { useDelegates } from '@/composables/delegate';

export enum ClaimType {
  ERC721 = 'erc721',
  ERC1155 = 'erc1155',
}

export type Creator = {
  id: number;
  address: string;
  name: string;
  image: string | null;
  twitterUrl?: string;
};

export type MintLimitType = 'whitelist' | 'limited' | 'open';

export type WalletRestrictionType = 'allowlist-limit' | 'wallet-limit' | 'no-limit'

export type InstanceResponse = {
  id: number;
  appId: number;
  slug: string;
  creator: Creator;
  publicData: {
    name: string;
    image: string;
    description: string;
    audienceId: number | null;
    extensionAddress: string;
    creatorContractAddress: string;
    claimIndex: number;
    claimType: string;
    /** tokenUrl is legacy, keeping it here for backward compatibility */
    tokenUrl?: string;
    /** storing animation moving forward, but legacy instances will not have this */
    animation?: string;
    networkId?: number;
  }
}

export const useClaimStore = defineStore('claim', () => {
  /**
   * STATE
   */

  /**
   * Note: Setting a default state is required. Many
   * values are overwritten at time of initialization but the
   * typings they establish are important.
   *
   * If after initialization a value is guaranteed to
   * not be `undefined`, set a default value here.
   */

  // instance data
  const id = ref(0);
  const name = ref('');
  const image = ref('');
  const description = ref('');
  const slug = ref('');
  const creator = ref<Creator>({} as Creator);
  const audienceId = ref<number | null>(null);
  const extensionAddress = ref('');
  const creatorContractAddress = ref('');
  const claimIndex = ref(0);
  const claimType = ref(ClaimType.ERC721);
  const tokenUrl = ref<string | undefined>();
  const animation = ref<string | undefined>();
  const fallbackProvider = ref<string>();
  const networkId = ref(1);

  // on-chain claim data
  const total = ref(0);
  const totalMax = ref<number | null>(null);
  const walletMax = ref<number | null>(null);
  const merkleInfo = ref<IMerkleInfo[]>([]);
  const startDate = ref<Date | null>(null);
  const endDate = ref<Date | null>(null);
  const storageProtocol = ref(StorageProtocol.ARWEAVE);
  const merkleRoot = ref(formatBytes32String(''));
  const location = ref('');
  const tokenId = ref<BigNumber | null>(null);
  const cost = ref(BigNumber.from(0));

  // store data
  const initialized = ref(false);
  const contract = ref({} as ClaimExtensionContract);
  const isLoadingWeb3State = ref(false);

  /**
   * The number of tokens the current wallet has minted.
   * - `null` if the claim has no wallet max or allow list (data is not tracked)
   * - otherwise the number of tokens minted by the wallet
   */
  const tokensMintedByWallet = ref<number | null>(null);
  const activeNetwork = ref<number>();
  const walletAddress = ref<string>();
  const isProviderAvailable = ref(false);

  /**
   * The number of allowlist mints available
   * for the current wallet
   */
  const claimableMintIndices = ref<number[]>([]);
  const claimableMerkleProofs = ref<string[][]>([]);

  /**
   * Wallet that user has selected to mint for on behalf of their logged in wallet (Delegation Registry). To learn more: https://delegate.cash/
   * This value is defaulted to the logged in wallet address.
   */
  const mintForWallet = ref<string>();

  /**
   * Composable to compute eligible vault wallets from delegation registry
   */
  const { isLoading: isDelegationLoading, eligibleVaultWallets, error: delegationError } = useDelegates(walletAddress, DELEGATION_REGISTRY_ADDRESS, { checkWalletsEligibility, checkAll: true });

  /**
   * ACTIONS
   */
  async function initialize (instance: InstanceResponse, fallbackProvider?: string) {
    _initializeInstanceData(instance);
    await _initializeProvider(fallbackProvider);

    contract.value = new ClaimExtensionContract(
      extensionAddress.value,
      creatorContractAddress.value,
      claimIndex.value
    );

    await refreshOnChainClaim();

    initialized.value = true;
  }

  async function _initializeProvider (fallbackProvider?: string) {
    await window.ManifoldEthereumProvider.initialize(networkId.value, fallbackProvider);
    isProviderAvailable.value = Boolean(window.ManifoldEthereumProvider.provider());

    setProviderEventsHandler(() => {
      const eth = window.ManifoldEthereumProvider;
      const provider = eth.provider();
      const address = eth.selectedAddress();
      const chainId = eth.chainId();

      walletAddress.value = address;
      // Default mintForWallet to walletAddress, this value can change if user selects a different wallet to mint for on UI
      mintForWallet.value = walletAddress.value;

      /**
       * NOTE: If a browser provider like MetaMask is available
       * chainId will be present, however with WalletConnect
       * there is often no provider available and therefore no chainId
       * either. We can only rely on a hardcoded value `NETWORK_ID`.
       */
      isProviderAvailable.value = Boolean(provider);
      activeNetwork.value = chainId || NETWORK_ID;
    });
  }

  function _initializeInstanceData (instance: InstanceResponse) {
    // TODO: use zod, this is all a lie
    id.value = instance.id;
    slug.value = instance.slug;
    creator.value = instance.creator;

    const publicData = instance.publicData;
    name.value = publicData.name;
    image.value = publicData.image;
    description.value = publicData.description;
    audienceId.value = publicData.audienceId;
    extensionAddress.value = publicData.extensionAddress;
    creatorContractAddress.value = publicData.creatorContractAddress;
    claimIndex.value = publicData.claimIndex;
    claimType.value = publicData.claimType.toLowerCase() as ClaimType;
    tokenUrl.value = publicData.tokenUrl;
    animation.value = publicData.animation;
    networkId.value = publicData.networkId || NETWORK_ID;
  }

  async function refreshOnChainClaim () {
    const onChainData = await contract.value.getClaim(claimType.value);
    total.value = onChainData.total;
    totalMax.value = onChainData.totalMax;
    walletMax.value = onChainData.walletMax;
    startDate.value = onChainData.startDate;
    endDate.value = onChainData.endDate;
    storageProtocol.value = onChainData.storageProtocol;
    merkleRoot.value = onChainData.merkleRoot;
    location.value = onChainData.location;
    tokenId.value = onChainData.tokenId;
    cost.value = onChainData.cost;
  }

  async function fetchMerkleInfo (address: string | undefined) {
    if (!address) {
      return [];
    }

    return await collectors.getMerkleInfos(
      slug.value,
      address
    );
  }

  async function fetchWeb3State () {
    isLoadingWeb3State.value = true;

    try {
      if (hasAllowlist.value) {
        merkleInfo.value = await fetchMerkleInfo(mintForWallet.value);
        const { mintIndices, merkleProofs } = await _fetchMintIndices(merkleInfo.value);
        claimableMintIndices.value = mintIndices;
        claimableMerkleProofs.value = merkleProofs;
      }

      refreshOnChainClaim();
      _refreshNumTokensMintedByWallet();

      // TODO: how to scope this refresh? identity-widget
      // doesn't need refreshing.
      window.dispatchEvent(new Event('m-refresh-widgets'));
    } finally {
      isLoadingWeb3State.value = false;
    }
  }

  /*
    Check if a certain wallet is eligible to claim one or more tokens
  */
  async function checkWalletsEligibility (addresses: string[]) : Promise<string[]> {
    // only check for eligibility if there's an allowlist (This is constrained on contract-level)
    const eligibleWallets : string[] = [];
    if (walletRestriction.value !== 'allowlist-limit') {
      return eligibleWallets;
    }

    for (const address of addresses) {
      const merkleInfo = await fetchMerkleInfo(address);
      const { mintIndices } = await _fetchMintIndices(merkleInfo);
      const allowedMintQuantity = _getClaimableQuantity(walletRestriction.value, mintIndices);
      if (allowedMintQuantity > 0) {
        eligibleWallets.push(address);
      }
    }
    return eligibleWallets;
  }

  /**
   * Private Methods
   */

  async function _refreshNumTokensMintedByWallet () {
    if (!mintForWallet.value) {
      return null;
    }

    if (walletRestriction.value === 'allowlist-limit') {
      const availableIndices = merkleInfo.value.map(
        (claimMerkleInfo) => claimMerkleInfo.value
      );
      const usedIndices = await contract.value.checkMintIndices(
        availableIndices
      );

      tokensMintedByWallet.value = usedIndices.filter(
        (mintedIndice) => mintedIndice === true
      ).length;
    } else if (walletRestriction.value === 'wallet-limit') {
      tokensMintedByWallet.value = await contract.value.getTotalMints(
        mintForWallet.value
      );
    } else {
      // claims with no wallet restrictions do not track
      // the amount of tokens minted per wallet.
      tokensMintedByWallet.value = null;
    }
  }

  function _getClaimableQuantity (walletRestriction: WalletRestrictionType, claimableMintIndices: number[]) {
    const totalSupply = totalMax.value === null ? Infinity : totalMax.value;

    // Bound by 0 as can be negative if creator updates total supply post mint
    const availableSupply = Math.max(0, totalSupply - total.value);

    let availableForWallet;
    switch (walletRestriction) {
      case 'allowlist-limit':
        availableForWallet = claimableMintIndices.length;
        break;
      case 'wallet-limit':
        // 1. can be negative if creator updates per wallet limit
        // 2. if we have a wallet limit, we always have a value for tokensMinted
        availableForWallet = Math.max(0, (walletMax.value as number) - tokensMintedByWallet.value!);
        break;
      case 'no-limit':
        availableForWallet = Infinity;
        break;
    }

    return Math.min(availableSupply, availableForWallet);
  }

  async function _fetchMintIndices (merkleInfo: IMerkleInfo[]) {
    if (!hasAllowlist.value) {
      return { mintIndices: [], merkleProofs: [] };
    }

    const mintIndices = merkleInfo.map((claimMerkleInfo) => claimMerkleInfo.value);
    const mintIndicesStatus = await contract.value.checkMintIndices(mintIndices);
    const claimableMerkleInfo = merkleInfo.filter((_, index) => !mintIndicesStatus[index]);

    return {
      mintIndices: claimableMerkleInfo.map((claimMerkleInfo) => claimMerkleInfo.value),
      merkleProofs: claimableMerkleInfo.map((claimMerkleInfo) => claimMerkleInfo.merkleProof)
    };
  }

  function setMintForWallet (address: string) {
    mintForWallet.value = address;
  }

  /**
   * GETTERS
   */
  const hasAllowlist = computed(() => {
    return merkleRoot.value !== formatBytes32String('');
  });

  const isConnected = computed(() => {
    return Boolean(walletAddress.value);
  });

  const hasWalletLimit = computed(() => {
    return walletMax.value !== null;
  });

  const isUnlimitedSupply = computed(() => {
    return totalMax.value === null;
  });

  const targetNetwork = computed(() => {
    // TODO: support isDev override
    // return isDev() ? network.value : networkId.value;
    return networkId.value;
  });

  const status = computed(() => {
    const now = Date.now();

    if (startDate.value && startDate.value.getTime() > now) {
      return 'not-started';
    } else if (endDate.value && endDate.value.getTime() < now) {
      return 'ended';
    } else {
      return 'active';
    }
  });

  const isSoldOut = computed(() => {
    if (totalMax.value === null) {
      return false;
    }

    // total can exceed totalMax if creator updates supply
    return total.value > 0 && total.value >= totalMax.value;
  });

  const isPayable = computed(() => {
    return cost.value.eq(0);
  });

  // TODO: use storage protocol?
  const arweaveURL = computed(() => {
    return `https://arweave.net/${location.value}`;
  });

  const is721 = computed(() => {
    return claimType.value === 'erc721';
  });

  const is1155 = computed(() => {
    return claimType.value === 'erc1155';
  });

  /**
   * A claim can be restricted by an allowlist (0x1 is allowed 4 mints)
   * or by wallet limit (ie: walletMax, '2 tokens max per wallet').
   * Otherwise there is no limit.
   *
   * Note: that a claim may still have a supply cap. This just
   * describes the wallet restriction.
   */
  const walletRestriction = computed(() : WalletRestrictionType => {
    if (hasAllowlist.value) {
      return 'allowlist-limit';
    } else if (walletMax.value) {
      return 'wallet-limit';
    } else {
      return 'no-limit';
    }
  });

  /**
   * Returns the number of tokens that can be claimed by the current wallet.
   * This is the minimum of the available supply and the amount available for
   * the wallet.
   *
   * @returns number, including Infinity
   */
  const claimableQuantity = computed(() => {
    return _getClaimableQuantity(walletRestriction.value, claimableMintIndices.value);
  });

  const isChainCorrect = computed(() => {
    return targetNetwork.value === activeNetwork.value;
  });

  /**
   * OTHER
   */
  watch(mintForWallet, () => {
    if (!mintForWallet.value || !initialized.value) {
      return;
    }
    fetchWeb3State();
  });

  watch(activeNetwork, () => {
    if (!activeNetwork.value || !initialized.value) {
      return;
    }
    fetchWeb3State();
  });

  watch(eligibleVaultWallets, () => {
    // default to the first eligible vault wallet, if the connected wallet is not eligible
    if (walletAddress.value && eligibleVaultWallets.value && eligibleVaultWallets.value.length > 0 && !eligibleVaultWallets?.value?.includes(walletAddress.value)) {
      setMintForWallet(eligibleVaultWallets.value[0]);
    }
  });

  return {
    initialize,
    fetchMerkleInfo,
    refreshOnChainClaim,
    fetchWeb3State,
    setMintForWallet,
    hasAllowlist,
    walletRestriction,
    isChainCorrect,
    isConnected,
    isUnlimitedSupply,
    hasWalletLimit,
    activeNetwork,
    walletAddress,
    mintForWallet,
    claimableQuantity,
    isSoldOut,
    status,
    arweaveURL,
    initialized,
    tokensMintedByWallet,
    targetNetwork,
    merkleInfo,
    id,
    name,
    image,
    description,
    contract,
    slug,
    audienceId,
    extensionAddress,
    creatorContractAddress,
    claimableMerkleProofs,
    claimableMintIndices,
    claimIndex,
    claimType,
    isPayable,
    tokenUrl,
    animation,
    fallbackProvider,
    creator,
    total,
    totalMax,
    walletMax,
    startDate,
    endDate,
    storageProtocol,
    merkleRoot,
    location,
    tokenId,
    cost,
    is721,
    is1155,
    isProviderAvailable,
    isLoadingWeb3State,
    isDelegationLoading,
    eligibleVaultWallets,
    delegationError
  };
});

export type State = ReturnType<typeof useClaimStore>;
